import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, AlertCircle, CheckCircle2, TrendingUp, Calendar, Info, Clock, DollarSign, ArrowLeft } from 'lucide-react';

interface Inputs {
  grossRevenue: number;
  commission: number;
  bookings: number;
  cleaningCost: number;
  utilities: number;
  insurance: number;
  investment: number;
  requestedRent: number;
}

export default function CalculadoraR2RPage() {
  const [inputs, setInputs] = useState<Inputs>({
    grossRevenue: 15000,
    commission: 15,
    bookings: 20,
    cleaningCost: 100,
    utilities: 50,
    insurance: 25,
    investment: 2000,
    requestedRent: 500,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const calc = useMemo(() => {
    const totalCommission = inputs.grossRevenue * (inputs.commission / 100);
    const totalCleaning = inputs.bookings * inputs.cleaningCost;
    const totalUtilities = inputs.utilities * 12;
    const totalInsurance = inputs.insurance * 12;
    const totalVariableExpenses = totalCommission + totalCleaning + totalUtilities + totalInsurance;

    const availableBagAnnual = inputs.grossRevenue - totalVariableExpenses;
    const availableBagMonthly = availableBagAnnual / 12;

    const maxRent = availableBagMonthly - 600;
    const actualProfitMonthly = availableBagMonthly - inputs.requestedRent;
    const actualProfitAnnual = actualProfitMonthly * 12;

    const paybackMonths = actualProfitMonthly > 0 ? inputs.investment / actualProfitMonthly : Infinity;
    const roiAnnual =
      inputs.investment > 0 && actualProfitMonthly > 0
        ? (actualProfitAnnual / inputs.investment) * 100
        : 0;

    const recommendedContractYears = Math.max(2, Math.ceil((paybackMonths * 4) / 12));

    return {
      totalVariableExpenses,
      availableBagAnnual,
      availableBagMonthly,
      maxRent,
      actualProfitMonthly,
      actualProfitAnnual,
      paybackMonths,
      roiAnnual,
      recommendedContractYears,
    };
  }, [inputs]);

  const getStatus = () => {
    if (calc.actualProfitMonthly >= 600) {
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: <CheckCircle2 className="w-8 h-8 text-green-500" />,
        text: 'Excelente Negocio',
        desc: 'Superas los 600€/mes, un margen ideal y seguro para turístico.',
      };
    } else if (calc.actualProfitMonthly >= 300) {
      return {
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        icon: <AlertCircle className="w-8 h-8 text-yellow-500" />,
        text: 'Viable pero Ajustado',
        desc: 'Ganas dinero, pero en modelo turístico podrías sufrir en los meses de temporada baja.',
      };
    } else if (calc.actualProfitMonthly > 0) {
      return {
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        icon: <AlertCircle className="w-8 h-8 text-orange-500" />,
        text: 'Alto Riesgo',
        desc: 'Margen peligroso. Cualquier mes malo te meterá en pérdidas.',
      };
    } else {
      return {
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: <AlertCircle className="w-8 h-8 text-red-500" />,
        text: 'Pérdidas (Inviable)',
        desc: 'Trabajarás gratis y perderás dinero cada mes.',
      };
    }
  };

  const status = getStatus();

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/calculadoras"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-sereno-dark transition-colors"
        >
          <ArrowLeft size={16} />
          Calculadoras
        </Link>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex items-center space-x-4">
        <div className="p-3 bg-blue-600 rounded-xl text-white">
          <Calculator className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calculadora de Viabilidad R2R</h1>
          <p className="text-slate-500">Evalúa la rentabilidad y el riesgo de tu modelo híbrido (Vacacional/Media Estancia)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Columna Izquierda: Inputs */}
        <div className="lg:col-span-5 space-y-6">
          {/* Sección 1: Ingresos */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
              1. Previsión de Ingresos
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Facturación Bruta Anual Estimada
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="grossRevenue"
                    value={inputs.grossRevenue}
                    onChange={handleChange}
                    className="w-full pl-3 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-400">€</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center" title="Porcentaje que se queda Airbnb, Booking, etc.">
                    Comisión Plat. <Info className="w-4 h-4 ml-1 text-slate-400" />
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="commission"
                      value={inputs.commission}
                      onChange={handleChange}
                      className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <span className="absolute right-3 top-2.5 text-slate-400">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nº Reservas/Año</label>
                  <input
                    type="number"
                    name="bookings"
                    value={inputs.bookings}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sección 2: Gastos Operativos */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-500" />
              2. Gastos Operativos
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Coste por Limpieza</label>
                <div className="relative">
                  <input
                    type="number"
                    name="cleaningCost"
                    value={inputs.cleaningCost}
                    onChange={handleChange}
                    className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-400">€</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Suministros/Mes</label>
                <div className="relative">
                  <input
                    type="number"
                    name="utilities"
                    value={inputs.utilities}
                    onChange={handleChange}
                    className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-400">€</span>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Imprevistos y Seguro / Mes</label>
                <div className="relative">
                  <input
                    type="number"
                    name="insurance"
                    value={inputs.insurance}
                    onChange={handleChange}
                    className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-400">€</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sección 3: Inversión y Negociación */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
              3. Inversión y Negociación
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium text-slate-700 mb-1"
                  title="Adecuación, mobiliario, fotos, licencias"
                >
                  Inversión Inicial (CAPEX) <Info className="inline w-4 h-4 ml-1 text-slate-400" />
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="investment"
                    value={inputs.investment}
                    onChange={handleChange}
                    className="w-full pl-3 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-400">€</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-indigo-700 font-bold mb-1">
                  Renta que pide el propietario
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="requestedRent"
                    value={inputs.requestedRent}
                    onChange={handleChange}
                    className="w-full pl-4 pr-8 py-3 border-2 border-indigo-400 rounded-lg focus:ring-2 focus:ring-indigo-600 outline-none bg-indigo-50 font-bold text-lg"
                  />
                  <span className="absolute right-4 top-3.5 text-indigo-600 font-bold">€</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Resultados */}
        <div className="lg:col-span-7 space-y-6">
          {/* Semáforo de Viabilidad */}
          <div className={`rounded-2xl p-6 border-2 ${status.border} ${status.bg} transition-colors duration-300`}>
            <div className="flex items-start space-x-4">
              <div className="mt-1">{status.icon}</div>
              <div>
                <h2 className={`text-xl font-bold ${status.color}`}>{status.text}</h2>
                <p className={`mt-1 ${status.color} opacity-80`}>{status.desc}</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-black/5">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wider mb-1">
                    Beneficio Neto Mensual
                  </p>
                  <p className={`text-5xl font-black ${calc.actualProfitMonthly > 0 ? 'text-slate-900' : 'text-red-600'}`}>
                    {calc.actualProfitMonthly.toFixed(0)}€
                  </p>
                  <p className="text-sm font-semibold text-slate-600 mt-2">
                    {calc.actualProfitAnnual > 0
                      ? `💰 +${calc.actualProfitAnnual.toFixed(0)}€ al año limpios`
                      : 'Estás asumiendo pérdidas anuales'}
                  </p>
                </div>
                <div className="text-right pb-2">
                  <p className="text-sm text-slate-500 mb-1">Renta Máx. Recomendada</p>
                  <p
                    className="text-2xl font-bold text-emerald-600"
                    title="Renta límite para asegurar al menos 600€/mes de colchón"
                  >
                    {calc.maxRent.toFixed(0)}€/mes
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payback y ROI */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center">
              <p className="text-sm font-medium text-slate-500 mb-1">Tiempo de Recuperación</p>
              {calc.paybackMonths === Infinity || calc.paybackMonths < 0 ? (
                <p className="text-2xl font-bold text-red-500">Nunca (Pérdidas)</p>
              ) : (
                <div>
                  <p className="text-3xl font-bold text-slate-900">
                    {calc.paybackMonths.toFixed(1)}{' '}
                    <span className="text-lg font-normal text-slate-500">meses</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Para recuperar tus {inputs.investment}€</p>
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center">
              <p className="text-sm font-medium text-slate-500 mb-1">ROI Anual (Retorno)</p>
              <p className="text-3xl font-bold text-slate-900">
                {calc.roiAnnual > 0 ? calc.roiAnnual.toFixed(0) : 0}%
              </p>
              <p className="text-xs text-slate-400 mt-1">Rentabilidad sobre tu inversión</p>
            </div>
          </div>

          {/* Duración de Contrato */}
          <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center mb-4">
              <Calendar className="w-6 h-6 mr-3 text-blue-400" />
              <h3 className="text-lg font-semibold">Duración de Contrato Sugerida</h3>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-4xl font-black text-blue-400">
                  {calc.paybackMonths > 0 && calc.paybackMonths !== Infinity
                    ? calc.recommendedContractYears
                    : '-'}{' '}
                  <span className="text-xl font-normal text-slate-300">años</span>
                </p>
                <p className="text-sm text-slate-400 mt-1">Mínimo necesario para proteger tus ganancias</p>
              </div>
            </div>
          </div>

          {/* Desglose */}
          <div className="bg-white rounded-xl p-5 border border-slate-200 text-sm shadow-sm">
            <h4 className="font-semibold text-slate-700 mb-4">¿De dónde salen estos números?</h4>
            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 pb-2 border-b border-slate-100">
              <div className="w-1/2">Concepto</div>
              <div className="w-1/4 text-right">Mensual</div>
              <div className="w-1/4 text-right">Anual</div>
            </div>
            <div className="space-y-3 text-slate-600">
              <div className="flex justify-between items-center">
                <span className="w-1/2 font-medium text-slate-700">Ingresos brutos medios</span>
                <span className="w-1/4 text-right font-medium">{(inputs.grossRevenue / 12).toFixed(0)}€</span>
                <span className="w-1/4 text-right text-slate-400">{inputs.grossRevenue.toFixed(0)}€</span>
              </div>
              <div className="flex justify-between items-center text-red-500">
                <span className="w-1/2">- Comis. y Gastos operativos</span>
                <span className="w-1/4 text-right">-{(calc.totalVariableExpenses / 12).toFixed(0)}€</span>
                <span className="w-1/4 text-right opacity-70">-{calc.totalVariableExpenses.toFixed(0)}€</span>
              </div>
              <div className="flex justify-between items-center border-t border-slate-100 pt-2 font-bold text-slate-800">
                <span className="w-1/2">= Bolsa (Antes de alquiler)</span>
                <span className="w-1/4 text-right">{calc.availableBagMonthly.toFixed(0)}€</span>
                <span className="w-1/4 text-right opacity-70">{calc.availableBagAnnual.toFixed(0)}€</span>
              </div>
              <div className="flex justify-between items-center text-indigo-600">
                <span className="w-1/2">- Renta exigida por dueño</span>
                <span className="w-1/4 text-right">-{inputs.requestedRent}€</span>
                <span className="w-1/4 text-right opacity-70">-{inputs.requestedRent * 12}€</span>
              </div>
              <div className="flex justify-between items-center border-t border-slate-100 pt-2 font-black text-slate-900 text-base">
                <span className="w-1/2">= Tu Beneficio Limpio</span>
                <span className="w-1/4 text-right">{calc.actualProfitMonthly.toFixed(0)}€</span>
                <span className="w-1/4 text-right text-sm opacity-80">{calc.actualProfitAnnual.toFixed(0)}€</span>
              </div>
            </div>
            <div className="mt-6 text-sm text-slate-600 border border-blue-100 pt-4 bg-blue-50/50 p-5 rounded-xl">
              <h5 className="font-semibold text-blue-800 mb-3 flex items-center">
                <Info className="w-5 h-5 mr-2" /> ¿Cuánto deberías ganar de margen?
              </h5>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>300€/mes:</strong> Es el estándar aceptable en una gestión{' '}
                  <strong>por habitaciones</strong>, ya que los ingresos son estables todos los meses.
                </li>
                <li>
                  <strong>600€ - 800€/mes:</strong> Es la ganancia objetivo en el modelo{' '}
                  <strong>turístico o híbrido</strong>. El sistema calcula la "Renta Recomendada" asumiendo
                  600€, ya que necesitas este colchón económico para compensar los meses de temporada baja en
                  los que no ganarás nada.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
